import { useEffect } from "react";
import { Button } from "components/elements";
import { cn } from "lib/cn";

type PollProps = {
    options: {
        id: number;
        content: string;
        percent?: number;
        votes?: number;
        myVote?: boolean;
    }[];
    handleVote: (optionId: number) => void;
    vote?: number | null;
    hasAlreadyVoted: boolean;
    setVote: (vote: number | null) => void;
    isPollIncluded: boolean;
    setIsVoted: (vote: boolean) => void;
    totalVotes?: number;
};

export const Poll = ({
    options,
    handleVote,
    vote,
    setVote,
    hasAlreadyVoted,
    isPollIncluded,
    totalVotes,
}: PollProps) => {
    // Depend on the full myVote signature rather than just length so the
    // selected vote re-syncs when setQueryData replaces the poll object.
    const myVoteKey = options.map((o) => `${o.id}:${o.myVote ? 1 : 0}`).join(",");
    useEffect(() => {
        if (options.length === 0) return;
        const mine = options.find((item) => item?.myVote);
        if (mine) setVote(mine.id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myVoteKey, setVote]);

    if (!isPollIncluded || !options.length) return <></>;

    const voteCount = totalVotes ?? 0;
    const voteWord = voteCount === 1 ? "vote" : "votes";

    return (
        <div className="flex flex-col items-start justify-center gap-3">
            <div className="text-sm font-medium text-slate-700">
                {hasAlreadyVoted ? "Poll Results" : "Cast Your Vote"}
            </div>

            <div className="text-xs text-slate-500">
                {hasAlreadyVoted
                    ? `${voteCount} ${voteWord}`
                    : `Vote to see the results · ${voteCount} ${voteWord} so far`}
            </div>

            <div className="text-normal flex w-full flex-col gap-2 font-medium">
                {options.map((option) => {
                    const percent = option.percent ?? 0;
                    const optionVotes = option.votes ?? 0;
                    const isMyVote = Boolean(option.myVote);
                    const isSelected = vote === option.id;
                    const clickable = !hasAlreadyVoted;

                    return (
                        <div
                            key={option.id}
                            className={cn(
                                "relative overflow-hidden rounded-md px-2 py-1.5",
                                clickable && "cursor-pointer",
                                isMyVote ? "bg-blue-50" : "bg-slate-100",
                            )}
                            onClick={() => {
                                if (!clickable) return;
                                setVote(isSelected ? null : option.id);
                            }}
                        >
                            {hasAlreadyVoted && (
                                <div
                                    className={cn(
                                        "absolute inset-y-0 left-0 z-0",
                                        isMyVote ? "bg-blue-200" : "bg-slate-200",
                                    )}
                                    style={{ width: `${percent}%` }}
                                />
                            )}
                            <div className="relative z-10 flex items-center gap-2">
                                {!hasAlreadyVoted && (
                                    <div
                                        className={cn(
                                            "flex h-4 w-4 shrink-0 rounded-full border border-slate-600 bg-slate-50",
                                            isSelected &&
                                                "border-blue-600 bg-blue-600 shadow-[0_0_0_2px_white_inset]",
                                        )}
                                    />
                                )}
                                <div className="flex-1">{option.content}</div>
                                {hasAlreadyVoted && (
                                    <div className="text-slate-600">
                                        {optionVotes} ({percent.toFixed(0)}%)
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {!hasAlreadyVoted && (
                <Button
                    disabled={vote === null || vote === undefined}
                    onClick={() => {
                        if (vote !== null && vote !== undefined) {
                            handleVote(vote);
                        }
                    }}
                >
                    Submit Vote
                </Button>
            )}
        </div>
    );
};
